// Admin API client. Talks to /api/admin/* endpoints (cookie-auth required).

import type { AuditEventRead, EngineOwnerRead, MethodRegistryEntryRead } from '../../types';

const PASSCODE_HEADER = 'x-demo-passcode';
const DEMO_PASSCODE = import.meta.env.VITE_DEMO_PASSCODE ?? 'arizona-c2-demo';

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
      [PASSCODE_HEADER]: DEMO_PASSCODE,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = (j as { detail?: string }).detail ?? detail;
    } catch {
      /* ignore */
    }
    throw new Error(`${res.status} ${detail}`);
  }
  return res.json() as Promise<T>;
}

export interface SystemInfo {
  uptime_s: number;
  memory_mb: number;
  db_size_kb: number;
  table_row_counts: Record<string, number>;
  cert_expires_at?: string;
  recent_restarts?: number;
}

export interface IntegrationsInfo {
  gemini: {
    configured: boolean;
    model: string;
    last_call_at: string | null;
    last_status: string | null;
    recent_calls: Array<{ ts: string; endpoint: string; latency_ms: number; status: string; output_preview: string }>;
  };
  sap: {
    connection_status: 'connected' | 'disconnected';
    endpoint: string;
    last_sync_at: string;
    sku_count: number;
    customer_count: number;
    open_orders: number;
    last_sync_duration_ms: number;
  };
}

export type Role = 'operator' | 'executive' | 'superadmin';

export interface AdminUser {
  id: string;
  email: string;
  role: Role;
  is_admin: boolean;  // derived (= superadmin); kept for backward compatibility
  created_at: string;
  last_login_at: string | null;
}

export interface EngineInfo {
  invocations: number;
  last_seed?: number;
  last_invocation_at?: string;
}

export const admin = {
  system: () => req<SystemInfo>('GET', '/api/admin/system'),
  integrations: () => req<IntegrationsInfo>('GET', '/api/admin/integrations'),
  sapSync: () => req<IntegrationsInfo['sap']>('POST', '/api/admin/integrations/sap/sync'),
  users: () => req<AdminUser[]>('GET', '/api/admin/users'),
  createUser: (email: string, password: string, role: Role) =>
    req<AdminUser>('POST', '/api/admin/users', { email, password, role }),
  updateUser: (id: string, patch: { email?: string; password?: string; role?: Role }) =>
    req<AdminUser>('PATCH', `/api/admin/users/${id}`, patch),
  deleteUser: (id: string) => req<{ status: string }>('DELETE', `/api/admin/users/${id}`),

  // RBAC (spec 12) — owners, audit, method registry (superadmin; the router is superadmin-gated)
  owners: () => req<EngineOwnerRead[]>('GET', '/api/admin/owners'),
  setOwner: (scope_type: 'engine' | 'intervention', scope_id: string, owner_user_id: string) =>
    req<EngineOwnerRead>('PUT', '/api/admin/owners', { scope_type, scope_id, owner_user_id }),
  removeOwner: (scope_type: string, scope_id: string) =>
    req<{ status: string }>('DELETE', `/api/admin/owners/${scope_type}/${encodeURIComponent(scope_id)}`),
  audit: (filters: Record<string, string | undefined> = {}) => {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, v);
    const query = qs.toString();
    return req<AuditEventRead[]>('GET', `/api/admin/audit${query ? `?${query}` : ''}`);
  },
  methodRegistry: () => req<MethodRegistryEntryRead[]>('GET', '/api/admin/method-registry'),
  engines: () => req<Record<string, EngineInfo>>('GET', '/api/admin/engines'),
  reset: () => req<{ status: string; reseeded_at: string }>('POST', '/api/admin/demo/reset'),
  reseed: () => req<{ status: string; reseeded_at: string }>('POST', '/api/admin/demo/reseed'),
  injectSignal: (signal_id: string, value: number) =>
    req<{ signal: unknown; breached: boolean; drift_packet_id: string | null }>(
      'POST',
      '/api/admin/demo/inject',
      { signal_id, value },
    ),
  connectors: () => req<ConnectorAdmin>('GET', '/api/admin/connectors'),
  setConnectorKey: (provider: string, api_key: string) =>
    req<ConnectorProvider>('PUT', `/api/admin/connectors/keys/${encodeURIComponent(provider)}`, { api_key }),
  clearConnectorKey: (provider: string) =>
    req<ConnectorProvider>('DELETE', `/api/admin/connectors/keys/${encodeURIComponent(provider)}`),
  setConnectorFeed: (commodity_id: string, enabled: boolean) =>
    req<ConnectorFeed>('PUT', `/api/admin/connectors/feeds/${encodeURIComponent(commodity_id)}`, { enabled }),

  // Connect AI provider (the language layer). The key is write-only: never returned, only last-4.
  llmStatus: () => req<LlmStatus>('GET', '/api/admin/llm/status'),
  llmSet: (provider: LlmProvider, key: string | null, model: string | null) =>
    req<LlmStatus>('POST', '/api/admin/llm/config', { provider, key, model }),
  llmClearKey: () => req<LlmStatus>('DELETE', '/api/admin/llm/key'),
  llmTest: () => req<LlmTestResult>('POST', '/api/admin/llm/test'),
  llmToggle: (edge: LlmEdge, enabled: boolean) =>
    req<LlmStatus>('POST', '/api/admin/llm/toggle', { edge, enabled }),
};

export type LlmProvider = 'openai' | 'anthropic' | 'gemini';
export type LlmEdge = 'ask' | 'explain' | 'ingest';
export interface LlmStatus {
  provider: string | null;
  model: string | null;
  model_set: string | null;  // the RAW stored model (null when unset), distinct from resolved model
  configured: boolean;
  key_last4: string | null;
  key_fingerprint: string | null;
  key_source: 'admin' | 'env' | 'none';
  edges: { ask: boolean; explain: boolean; ingest: boolean };
  last_test: { ok: boolean | null; at: string | null; latency_ms: number | null };
}
export interface LlmTestResult {
  ok: boolean;
  latency_ms: number | null;
  error: string | null;
  provider: string | null;  // the SAVED provider the test actually validated
}

export interface ConnectorProvider {
  provider: string;
  key_source: 'admin' | 'env' | 'none';
  updated_at: string | null;
}
export interface ConnectorFeed {
  commodity_id: string;
  label: string;
  provider: string;
  enabled: boolean;
}
export interface ConnectorAdmin {
  providers: ConnectorProvider[];
  feeds: ConnectorFeed[];
}
